import React, { useEffect, useState, useCallback } from "react";
import "../../admin/AdminDashboard.css";
import { db, auth } from "../../firebase";
import { collection, getDocs, updateDoc, doc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

export default function TrailsEditorDashboard() {
  const [loading, setLoading] = useState(true);
  const [trailCount, setTrailCount] = useState(0);
  const [trails, setTrails] = useState([]);
  const [userData, setUserData] = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  const navigate = useNavigate();

  // Fetch user permissions
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const usersRef = collection(db, "users");
          const usersSnap = await getDocs(usersRef);
          
          let userDoc = null;
          usersSnap.forEach((doc) => {
            if (doc.data().uid === user.uid || doc.id === user.uid) {
              userDoc = {
                id: doc.id,
                uid: user.uid,
                ...doc.data(),
              };
            }
          });

          if (userDoc) {
            setUserData(userDoc);
            if (userDoc.role !== "admin" && userDoc.role !== "editor") {
              alert("You don't have permission to access this page");
              navigate("/dashboard");
            }
          } else {
            navigate("/dashboard");
          }
        } catch (error) {
          navigate("/dashboard");
        }
      } else {
        navigate("/login");
      }
      setUserLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  function filterTrailsByPermissions(allTrails) {
    if (!userData) return [];
    
    if (userData.role === "admin") {
      return allTrails;
    }

    if (userData.role === "editor") {
      const allowedCounties = userData.allowedCounties || [];
      return allTrails.filter(trail => allowedCounties.includes(trail.city));
    }

    return [];
  }

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      
      const trailsSnap = await getDocs(collection(db, "trails"));
      const allTrails = trailsSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const filteredTrails = filterTrailsByPermissions(allTrails);
      
      setTrailCount(filteredTrails.length);
      setTrails(filteredTrails);
    } catch (err) {
      // Silent fail
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  useEffect(() => {
    if (!userLoading && userData) {
      fetchStats();
    }
  }, [userLoading, userData, fetchStats]);

  function canEditCounty(county) {
    if (!userData) return false;
    if (userData.role === "admin") return true;
    if (userData.role === "editor") {
      return (userData.allowedCounties || []).includes(county);
    }
    return false;
  }

  const toggleLaunchStatus = async (trailId, currentStatus, county) => {
    if (!canEditCounty(county)) {
      alert(`You don't have permission to edit trails in ${county}`);
      return;
    }

    try {
      const trailRef = doc(db, "trails", trailId);
      await updateDoc(trailRef, {
        isLaunched: !currentStatus,
      });

      setTrails((prevTrails) =>
        prevTrails.map((trail) =>
          trail.id === trailId
            ? { ...trail, isLaunched: !currentStatus }
            : trail
        )
      );
    } catch (err) {
      alert("Error updating trail status. Please try again.");
    }
  };

  function handleEditTrail(trailId, county) {
    if (!canEditCounty(county)) {
      alert(`You don't have permission to edit trails in ${county}`);
      return;
    }
    navigate(`/trailseditor/${trailId}/edit`);
  }

  function handleManageClues(trailId, county) {
    if (!canEditCounty(county)) {
      alert(`You don't have permission to manage clues for trails in ${county}`);
      return;
    }
    navigate(`/trailseditor/${trailId}/clues`);
  }

  if (userLoading || loading) {
    return (
      <div className="dash-page">
        <div className="dash-loader">
          <div className="spinner"></div>
          <p>Loading trails...</p>
        </div>
      </div>
    );
  }

  const isAdmin = userData?.role === "admin";
  const allowedCounties = userData?.allowedCounties || [];

  return (
    <div className="dash-page">
      <div className="dash-container">
        <header className="dash-header">
          <div className="dash-header-content">
            <h1 className="dash-title">Trails Editor</h1>
            <p className="dash-subtitle">
              {isAdmin 
                ? "Manage all mystery trails"
                : `Managing trails in: ${allowedCounties.join(", ")}`
              }
            </p>
          </div>
          {userData && (
            <div className="user-role-badge">
              <span className={`role-indicator ${userData.role}`}>
                {userData.role === "admin" ? "Admin" : "Editor"}
              </span>
            </div>
          )}
        </header>

        {/* Stats Grid */}
        <div className="admin-stats-grid">
          <div className="dash-card stat-card">
            <div className="stat-icon trails">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="stat-content">
              <h3>{isAdmin ? "Total Trails" : "Your Trails"}</h3>
              <p className="stat-number">{trailCount}</p>
            </div>
          </div>

          <div className="dash-card stat-card">
            <div className="stat-icon clues">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="stat-content">
              <h3>Live Trails</h3>
              <p className="stat-number">{trails.filter(t => t.isLaunched).length}</p>
            </div>
          </div>

          <div className="dash-card stat-card">
            <div className="stat-icon users">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="stat-content">
              <h3>Draft Trails</h3>
              <p className="stat-number">{trails.filter(t => !t.isLaunched).length}</p>
            </div>
          </div>
        </div>

        {/* County Access Info for Editors */}
        {!isAdmin && allowedCounties.length > 0 && (
          <div className="county-access-card">
            <div className="card-header">
              <h2>Your Assigned Counties</h2>
              <p>You can create and edit trails in these counties</p>
            </div>
            <div className="county-badges">
              {allowedCounties.map(county => (
                <span key={county} className="county-badge">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  {county}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Trails Section */}
        <section className="trails-section">
          <div className="section-header">
            <div>
              <h2>Mystery Trails</h2>
              <p className="section-subtitle">
                {isAdmin 
                  ? "All mystery trails currently in the system"
                  : "Trails you can manage"
                }
              </p>
            </div>
            <button
              className="dash-btn primary"
              onClick={() => navigate("/trailseditor/new")}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '8px' }}>
                <path d="M8 1V15M1 8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Add New Trail
            </button>
          </div>

          {trails.length === 0 ? (
            <div className="empty-state">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p>
                {isAdmin 
                  ? "No trails available yet"
                  : "No trails in your assigned counties yet"
                }
              </p>
            </div>
          ) : (
            <div className="trails-grid">
              {trails.map((trail) => (
                <div key={trail.id} className="trail-card">
                  <div className="trail-header">
                    <h3>{trail.name}</h3>
                    {trail.isPaid ? (
                      <span className="trail-badge paid">
                        Paid • €{trail.priceEUR || 0}
                      </span>
                    ) : (
                      <span className="trail-badge free">Free</span>
                    )}
                  </div>

                  <p className="trail-description">{trail.description}</p>

                  <div className="trail-meta">
                    <div className="meta-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      <span>{trail.city}</span>
                    </div>
                    <div className="meta-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M21 16V8C20.9996 7.64927 20.9071 7.30481 20.7315 7.00116C20.556 6.69751 20.3037 6.44536 20 6.27L13 2.27C12.696 2.09446 12.3511 2.00205 12 2.00205C11.6489 2.00205 11.304 2.09446 11 2.27L4 6.27C3.69626 6.44536 3.44398 6.69751 3.26846 7.00116C3.09294 7.30481 3.00036 7.64927 3 8V16C3.00036 16.3507 3.09294 16.6952 3.26846 16.9988C3.44398 17.3025 3.69626 17.5546 4 17.73L11 21.73C11.304 21.9055 11.6489 21.9979 12 21.9979C12.3511 21.9979 12.696 21.9055 13 21.73L20 17.73C20.3037 17.5546 20.556 17.3025 20.7315 16.9988C20.9071 16.6952 20.9996 16.3507 21 16Z" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      <span>{trail.mapType}</span>
                    </div>
                  </div>

                  <div className="trail-status">
                    <button
                      className={`status-toggle ${trail.isLaunched ? 'live' : 'offline'}`}
                      onClick={() => toggleLaunchStatus(trail.id, trail.isLaunched, trail.city)}
                    >
                      <span className="status-indicator"></span>
                      {trail.isLaunched ? 'Live' : 'Offline'}
                    </button>
                  </div>

                  <div className="trail-actions">
                    <button
                      className="dash-btn secondary"
                      onClick={() => handleManageClues(trail.id, trail.city)}
                    >
                      Manage Clues
                    </button>
                    <button
                      className="dash-btn primary"
                      onClick={() => handleEditTrail(trail.id, trail.city)}
                    >
                      Edit Trail
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}